import React, { useState, useRef, useEffect } from "react";
import {
	Avatar,
	Button,
	Chip,
	TextField,
	TableContainer,
	Paper,
	Table,
	TableRow,
	TableBody,
	TableCell,
	TableHead,
	Link,
	FormHelperText,
	Grid,
	Tooltip,
} from "@mui/material";
import { chipCloseDefaultIcon, uploadPrimaryIcon } from "assets/images";
import ResponsiveDialog from "components/ResponsiveDialog";
import ColumnAction from "components/ResponsiveTable/ColumnAction";
import { DocumentUploadProps } from "./DocumentUpload.Props";
import { IDocument } from "models/DocumentModel";
import ConfirmationDialog from "components/ConfirmationDialog";
import { Shared } from "utility/shared";
import { ActionType } from "utility/enums/action-type";
import environment from "environment";

interface IFile {
	fileName: string;
	file: File;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
	open,
	documentList,
	isFileUploaded = false,
	isDisabled = false,
	stepNumber,
	sequenceNumber = 0,
	isAttachmentViewOnly = false,
	showFinancialYear = false,
	handleUploadDocument,
	handleDeleteDocument,
	handleDialogClose,
	showStepNumber = true,
	isReqToBidConversion = false,
}) => {
	const maxFileSize = environment.documentAttachmentSize;
	const fileUpload = useRef<HTMLInputElement | null>(null);
	const [selectedFile, setSelectedFile] = React.useState<IFile | null>(null);
	const [description, setDescription] = React.useState("");
	const [openConfirmationDialog, setOpenConfirmationDialog] =
		React.useState(false);
	const [selectedIndex, setSelectedIndex] = useState<number>(-1);
	const [highlightRequiredDetail, setHighlightRequiredDetail] =
		useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [tooltipIndex, setTooltipIndex] = React.useState<number>(-1);

	const isDescLengthInvalid = !!description && description.length > 100;

	const deleteSelectedFile = () => {
		resetFileInputValue();
		setSelectedFile(null);
	};
	const resetFileInputValue = () => {
		// reset input file ref value
		if (fileUpload && fileUpload.current) {
			fileUpload.current.value = "";
		}
	};

	useEffect(() => {
		if (isFileUploaded) {
			setHighlightRequiredDetail(false);
			setDescription("");
			resetFileInputValue();
			setSelectedFile(null);
		}
	}, [isFileUploaded]);

	const handleFileSelectionChange = (event: any) => {
		setErrorMessage("");
		const selectedFile = event.target.files[0];
		if (selectedFile) {
			const isFileTypeValid =
				Shared.mimeTypeList.indexOf(selectedFile.type) > -1;
			const isFileSizeValid = selectedFile.size / 1024 / 1024 < maxFileSize;
			const isFileNameSizeValid = Shared.isFileNameSizeValid(selectedFile.name);
			if (isFileTypeValid && isFileSizeValid && isFileNameSizeValid) {
				setSelectedFile({
					fileName: selectedFile.name,
					file: selectedFile,
				});
			} else {
				setErrorMessage(
					!isFileTypeValid
						? `${selectedFile.name.substr(
								selectedFile.name.lastIndexOf(".") + 1
						  )} files are not allowed.`
						: !isFileNameSizeValid
						? "The file name is too long. Rename the file and upload again!"
						: `The file size must be less than ${maxFileSize}MB`
				);
				setSelectedFile(null);
			}
			resetFileInputValue();
		}
	};

	const handleAddDocument = () => {
		setHighlightRequiredDetail(true);
		setErrorMessage("");
		if (!description || isDescLengthInvalid || !selectedFile?.file) {
			return;
		}
		handleUploadDocument(description, selectedFile?.file);
	};

	const deleteDocument = (index: number) => {
		setSelectedIndex(index);
		setOpenConfirmationDialog(true);
	};

	const handleConfirmationDialogClose = (isDelete = false) => {
		if (isDelete) {
			handleDeleteDocument(documentList[selectedIndex].id);
		}
		setOpenConfirmationDialog(false);
	};

	const downloadDocument = (event: any, url: string) => {
		event.preventDefault();
		window.open(url, "_blank");
	};

	return (
		<>
			<ResponsiveDialog
				open={open}
				maxWidth={stepNumber || sequenceNumber > 0 ? "md" : "sm"}
				showActionButtons={false}
				onCancel={handleDialogClose}
				title="Attachments"
			>
				{!isAttachmentViewOnly && (
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={12} sm="auto">
									<label
										htmlFor="contained-button-file"
										className="upload"
										title="DOC, DOCX, PDF, TXT, JPG, JPEG, PNG files are allowed"
										onClick={(e) => e.stopPropagation()}
									>
										<input
											id="contained-button-file"
											type="file"
											ref={fileUpload}
											accept={Shared.mimeTypeList.join(",")}
											onChange={(event: any) =>
												handleFileSelectionChange(event)
											}
										/>
										<Button
											variant="outlined"
											component="span"
											startIcon={
												<Avatar
													src={uploadPrimaryIcon}
													variant="square"
													alt="Upload"
												/>
											}
											onClick={(e: any) => {
												e.stopPropagation(); // stop Link from intercepting
												setErrorMessage("");
											}}
										>
											Browse
										</Button>
									</label>
									{((highlightRequiredDetail && !selectedFile?.fileName) ||
										errorMessage) && (
										<FormHelperText error>
											{errorMessage
												? errorMessage
												: highlightRequiredDetail &&
												  !selectedFile?.fileName &&
												  "Please select file"}
										</FormHelperText>
									)}
								</Grid>
								{selectedFile && (
									<Grid item xs={12} sm>
										<Chip
											label={selectedFile.fileName}
											onDelete={deleteSelectedFile}
											deleteIcon={
												<Avatar
													src={chipCloseDefaultIcon}
													alt="Close"
													variant="square"
												/>
											}
										/>
									</Grid>
								)}
							</Grid>
						</Grid>
						<Grid item xs={12} sm>
							<TextField
								label="Description"
								variant="outlined"
								value={description}
								autoComplete="off"
								onChange={(event: any) => setDescription(event.target.value)}
								error={
									(highlightRequiredDetail && !description) ||
									isDescLengthInvalid
								}
								helperText={
									(highlightRequiredDetail &&
										!description &&
										"Please enter supporting document description") ||
									(isDescLengthInvalid &&
										"Description can be max 100 characters long")
								}
								onBlur={(e) => setDescription(e.target.value.trim())}
							/>
						</Grid>
						<Grid item xs={12} sm="auto">
							<Button
								variant="contained"
								onClick={handleAddDocument}
								disabled={isDisabled}
							>
								Add
							</Button>
						</Grid>
					</Grid>
				)}
				<TableContainer
					component={Paper}
					className={!isAttachmentViewOnly ? "attachment-modal-table" : ""}
				>
					<div className="table-responsive radius-4">
						<Table
							className="primary-table"
							style={{ borderCollapse: "separate" }}
						>
							<TableHead>
								<TableRow>
									{isReqToBidConversion && (
										<TableCell>Requisition Number</TableCell>
									)}
									<TableCell>File Name</TableCell>
									<TableCell>Description</TableCell>
									{showFinancialYear && (
										<TableCell align="center">Financial Year</TableCell>
									)}
									{showStepNumber && stepNumber ? (
										<TableCell align="center">Step No.</TableCell>
									) : (
										<></>
									)}
									{sequenceNumber > 0 && (
										<TableCell align="center">Sequence No.</TableCell>
									)}
									<TableCell align="center">Action</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!documentList.length && (
									<TableRow>
										<TableCell
											colSpan={stepNumber && sequenceNumber > 0 ? 5 : 3}
											align="center"
										>
											No Data Available
										</TableCell>
									</TableRow>
								)}
								{documentList.map((document: IDocument, index: number) => (
									<TableRow key={document.id}>
										{isReqToBidConversion && (
											<TableCell>{document.requisitionNumber || "-"}</TableCell>
										)}
										<TableCell
											className={
												document.sequenceNumber === sequenceNumber &&
												document.stepNumber === stepNumber
													? "req-line-update"
													: "req-line-default"
											}
										>
											<Link
												href=""
												title={document.documentFileName}
												underline="hover"
												onClick={(event) =>
													downloadDocument(event, document.documentFilePath)
												}
											>
												{document.documentFileName}
											</Link>
										</TableCell>
										<TableCell>
											<Tooltip
												title={document.documentDescription}
												open={tooltipIndex === index}
												arrow
												PopperProps={{
													className: "long-text-tooltip",
												}}
												placement="top"
												onOpen={() => setTooltipIndex(index)}
												onClose={() => setTooltipIndex(-1)}
											>
												<Button
													disableRipple
													className="long-text-tooltip"
													variant="text"
													color="primary"
													onClick={() =>
														setTooltipIndex(tooltipIndex !== index ? index : -1)
													}
												>
													{document.documentDescription}
												</Button>
											</Tooltip>
										</TableCell>
										{showFinancialYear && (
											<TableCell align="center">
												{document.financialYear || "-"}
											</TableCell>
										)}
										{showStepNumber && stepNumber ? (
											<TableCell align="center">
												{document.stepNumber || "-"}
											</TableCell>
										) : (
											<></>
										)}
										{sequenceNumber > 0 && (
											<TableCell align="center">
												{document.sequenceNumber || "-"}
											</TableCell>
										)}
										<TableCell align="center">
											{!document.isReadOnly &&
											!isAttachmentViewOnly &&
											document.requisitionNumber === null ? (
												<ColumnAction
													menuItems={[ActionType.Delete]}
													iconClass="menu-icon"
													handleMenuItemClick={() => deleteDocument(index)}
												/>
											) : (
												"-"
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</TableContainer>
			</ResponsiveDialog>
			{openConfirmationDialog && (
				<ConfirmationDialog
					open={openConfirmationDialog}
					title="Attachment Delete Confirmation"
					message={`Are you sure you want to delete ${documentList[selectedIndex].documentFileName}?`}
					handleDialogClose={handleConfirmationDialogClose}
				/>
			)}
		</>
	);
};

export default DocumentUpload;
